import { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactElement, RefObject } from 'react';

import { AppColors, whiteAlpha } from '@/config/appColors';
import { AppStyles, withHeight } from '@/config/appStyles';
import type {
  AssistantMessage,
  ChatMessage,
} from '@/presentation/providers/chatController/chatMessage';
import { MaybeHero } from '@/presentation/widgets/MaybeHero';
import { formatCssColor, parseCssColor } from '@/utils/extensions/colorExtension';
import { wv } from '@/utils/screenUtil';
import { calculateLineCount } from '@/utils/sizeUtil';
import { useIsLandscape } from '@/utils/useScreen';

import { ChatMessageSourceTile } from './ChatMessageSourceTile';
import { useAnimatedSize } from './useAnimatedSize';
import styles from './ChatMessageTile.module.css';

/**
 * Перенос `chat_page/chat_message_tile.dart`.
 *
 * `placeholderBuilder` у Hero не переносился: во Flutter он рисовал копию
 * пузыря на месте улетевшего, а [MaybeHero] в вебе делает перелёт приёмом
 * FLIP — исходный элемент никуда не девается, подменять нечего.
 */

/** `AppStyles.s20w400.copyWith(height: 24 / 20)` */
const MESSAGE_STYLE = withHeight(AppStyles.s20w400, 24 / 20);

/** Ширина пузыря в макете и ширины самих макетов — из формулы Dart. */
const MESSAGE_DESIGN_WIDTH = 672;
const LANDSCAPE_DESIGN_WIDTH = 1366;
const PORTRAIT_DESIGN_WIDTH = 1010;

/** `AnimatedSize(duration: Duration(milliseconds: 200))` */
const ANIMATED_SIZE_DURATION_MS = 200;

/** `AppColors.c444D80.withValues(alpha: 0.5)` — фон вопроса посетителя. */
const MY_BACKGROUND = formatCssColor({
  ...parseCssColor(AppColors.c444D80),
  alpha: 0.5,
});

/** `Colors.white.withValues(alpha: 0.2)` — фон ответа аватара. */
const ASSISTANT_BACKGROUND = whiteAlpha(0.2);

/** Одна строка текста — отступ сверху и снизу вдвое меньше. */
const SINGLE_LINE_PADDING = 8;
const MULTI_LINE_PADDING = 16;
const SINGLE_LINE = 1;

/**
 * `LayoutBuilder` из Dart: ширина, доступная сообщению.
 *
 * Пока замера нет, ширина равна нулю — на этот кадр `max-width` пузыря
 * берётся из CSS (та же доля макета), так что на экране разницы не видно.
 * Замер нужен только для [calculateLineCount].
 */
function useMeasuredWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) return undefined;
    const read = (): void => setWidth(element.getBoundingClientRect().width);
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(read);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/** `message is AssistantMessage && message.chunks.isNotEmpty` */
function sourcesOf(message: ChatMessage): AssistantMessage | null {
  if (message.role !== 'assistant' || message.chunks.length === 0) return null;
  return message;
}

export interface ChatMessageTileProps {
  readonly message: ChatMessage;
  readonly enableHero?: boolean;
  readonly selectedMessage?: number | null;
  readonly onSourcePressed?:
    | ((hero: string, message: AssistantMessage) => void)
    | null;
}

export function ChatMessageTile(props: ChatMessageTileProps): ReactElement {
  const { message, enableHero = true, selectedMessage, onSourcePressed } = props;
  const isLandscape = useIsLandscape();
  const rowRef = useRef<HTMLDivElement | null>(null);
  const maxWidth = useMeasuredWidth(rowRef);
  const { outerRef, innerRef } = useAnimatedSize({
    durationMs: ANIMATED_SIZE_DURATION_MS,
  });

  const hero = `chat-message-${message.id}`;
  const isMy = message.isMy;
  const makeOpacity =
    selectedMessage !== null &&
    selectedMessage !== undefined &&
    selectedMessage !== message.id;
  const designWidth = isLandscape ? LANDSCAPE_DESIGN_WIDTH : PORTRAIT_DESIGN_WIDTH;
  const messageMaxWidth = maxWidth * (MESSAGE_DESIGN_WIDTH / designWidth);
  const messageLineCount = calculateLineCount(
    message.message,
    MESSAGE_STYLE,
    messageMaxWidth,
  );
  const withSources = sourcesOf(message);
  const style = {
    // `vertical: messageLineCount == 1 ? 8.w : 16.w`
    '--message-padding-y': wv(
      messageLineCount === SINGLE_LINE ? SINGLE_LINE_PADDING : MULTI_LINE_PADDING,
    ),
    '--message-background': isMy ? MY_BACKGROUND : ASSISTANT_BACKGROUND,
  } as CSSProperties;

  return (
    <MaybeHero tag={enableHero ? hero : null} className={styles.hero}>
      <div
        ref={rowRef}
        className={`${styles.row} ${isMy ? styles.my : styles.assistant}`}
        style={style}
      >
        <div
          className={[
            styles.bubble,
            makeOpacity ? styles.dimmed : null,
            withSources === null ? null : styles.withSources,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className={styles.backdrop}>
            <div ref={outerRef} className={styles.container}>
              <div ref={innerRef} className={styles.inner}>
                <span className={styles.text}>{message.message}</span>
                {withSources !== null && (
                  <ChatMessageSourceTile
                    chunks={withSources.chunks}
                    onPressed={
                      onSourcePressed == null
                        ? null
                        : () => onSourcePressed(hero, withSources)
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MaybeHero>
  );
}
